import React, { useEffect } from 'react'
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from './interceptors';
import { validateEmail, validateOTP, validatePassword } from '../auth/validationHelpers';
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PasswordInput from '../input/PasswordField';
import cn from '../../utils/cn';
import { validateField } from '../../helpers/validationStratergy';
import { tagsToAvoidOnBlurEvent, toastStyle } from '../../utils/toastUtils';
import InputField from '../input/InputField';

function ResetPassword() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    password: ""
  });
  const [errors, setErrors] = useState({
    email: "",
    otp: "",
    password: "",
    apiError: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => timer - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value),
      apiError: ""
    }));

  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const { email } = formData;

    setErrors({ ...errors, apiError: "" });

    const emailError = validateEmail(email);
    setErrors({ ...errors, email: emailError });
    if (emailError) {
      return;
    }

    const payload = { email: formData.email };

    try {
      setIsLoading(true);
      await api.post('/api/request-reset-password/', payload);

      toast.success("OTP sent to your email successfully!", { style: toastStyle });
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      console.error(err);
      const errorMsg = err?.response?.data?.msg || "Failed to send OTP. Please try again.";
      toast.error(errorMsg, { style: toastStyle });
      setErrors({ ...errors, apiError: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const { email, otp, password } = formData;

    setErrors({ ...errors, apiError: "" });

    const newErrors = {
      email: validateEmail(email),
      otp: validateOTP(otp),
      password: validatePassword(password)
    };

    setErrors({ ...errors, ...newErrors });
    if (Object.values(newErrors).some(error => error)) return;

    const payload = {
      email: formData.email,
      otp: formData.otp,
      new_password: formData.password
    };

    try {
      setIsLoading(true);
      await api.post('/api/reset-password/', payload);

      toast.success("Password reset successfully! Redirecting back to login page", { style: toastStyle });
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      console.error(err);
      const errorMsg = err?.response?.data?.msg || "Invalid OTP or failed to reset password";
      toast.error(errorMsg, { style: toastStyle });
      setErrors({ ...errors, apiError: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    const payload = { email: formData.email };

    try {
      await api.post('/api/request-reset-password/', payload);

      toast.success("OTP resent successfully!", { style: toastStyle });
      setResendTimer(60);
    } catch (err) {
      console.error(err);
      const errorMsg = err?.response?.data?.msg || "Failed to resend OTP";
      toast.error(errorMsg, { style: toastStyle });
    }
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      const nextElement = document.activeElement;
      if (!tagsToAvoidOnBlurEvent.includes(nextElement.tagName)) {
        handleChange(e);
      }
    }, 0);
  };

  const goBackToEmailStep = () => {
    setStep(1);
    setFormData({ ...formData, otp: "", password: "" });
    setErrors({ email: "", otp: "", password: "", apiError: "" });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <img
        src="/sift-hire-logo.png"
        alt="Logo"
        className="logo w-48 mb-4 sm:mb-6"
      />
      <div className="w-full mx-auto max-w-md">
        <div className="bg-white px-6 py-8 sm:px-12 shadow sm:rounded-lg border border-accent-gray-border">
          <div className="mb-4">
            <div className="flex items-center justify-center space-x-2 sm:space-x-4">
              <button
                type="button"
                onClick={() => step === 2 && goBackToEmailStep()}
                className={cn("flex items-center transition-colors",
                  step >= 1 ? "text-accent-blue" : "text-accent-gray",
                  step === 2 ? "cursor-pointer hover:text-accent-blue" : "cursor-default"
                )}
              >
                <div className={cn("w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold",
                  step >= 1 ? "bg-accent-blue text-white" : "bg-gray-200 text-accent-gray-dark")}>
                  1
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium">Email</span>
              </button>
              <div className={cn("w-4 sm:w-8 h-0.5", step >= 2 ? "bg-accent-blue" : "bg-gray-200")}></div>
              <div className={cn("flex items-center", step >= 2 ? "text-accent-blue" : "text-accent-gray")}>
                <div className={cn("w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold",
                  step >= 2 ? "bg-accent-blue text-white" : "bg-gray-200 text-accent-gray-dark")}>
                  2
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium">Verify & Reset</span>
              </div>
            </div>
          </div>

          {step === 1 && (
            <form onSubmit={handleSendOTP}>
              <div className="text-center mb-4">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Reset Password</h1>
                <p className="text-accent-gray-dark text-xs sm:text-sm mt-2">Enter your email address to receive an OTP</p>
              </div>

              <InputField
                id="email"
                name="email"
                placeholder="Enter your email address"
                type="name"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                autoFocus
                errorState={errors.email}
                label={"Email Address"}
              />

              <div className="mt-4 sm:mt-6 flex">
                <span className="block w-full rounded-md shadow-sm">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={cn("w-full flex justify-center items-center gap-2 py-2 sm:py-3 px-4 text-base sm:text-xl font-semibold rounded-md text-white transition duration-150 ease-in-out",
                      isLoading
                        ? "bg-accent-blue cursor-not-allowed"
                        : "bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:border-accent-blue focus:shadow-outline-blue active:bg-accent-blue-shadow")}
                  >
                    {isLoading ? (
                      <>
                        <span>Sending OTP</span>
                        <div className="animate-spin rounded-full border-t-2 border-white h-4 w-4"></div>
                      </>
                    ) : (
                      <>
                        <span>Send OTP</span>
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19.5 3.5H5.5C4.39543 3.5 3.5 4.39543 3.5 5.5V19.5C3.5 20.6046 4.39543 21.5 5.5 21.5H19.5C20.6046 21.5 21.5 20.6046 21.5 19.5V5.5C21.5 4.39543 20.6046 3.5 19.5 3.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8.5 12.5H16.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12.5 16.5L16.5 12.5L12.5 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </button>
                </span>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleResetPassword}>
              <div className="flex flex-col justify-center items-center mb-4">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Verify & Reset</h1>
                <p className="flex gap-1 text-accent-gray-dark text-xs sm:text-sm mt-2 px-2">
                  <span className="whitespace-nowrap flex-shrink-0">OTP sent to</span>
                  <strong className="break-all">{formData.email}</strong>
                </p>
              </div>

              <InputField
                id="otp"
                name="otp"
                placeholder="Enter 6-digit OTP"
                type="text"
                value={formData.otp}
                onChange={handleChange}
                onBlur={handleBlur}
                autoFocus
                errorState={errors.otp}
                label={"OTP"}
                maxLength={6}
              />

              <div className="text-right">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={resendTimer > 0 || isLoading}
                  className={cn("text-xs sm:text-sm",
                    resendTimer > 0 || isLoading
                      ? "text-accent-gray cursor-not-allowed"
                      : "text-accent-blue hover:underline"
                  )}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                </button>
              </div>

              <div>
                <PasswordInput
                  name={"password"}
                  placeholder='Enter your new password'
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorState={errors.password}
                  label={"New Password"}
                  isPasswordToggle={true}
                  aria-label='Enter your new password'
                />
              </div>

              <div className="mt-4 flex">
                <span className="block w-full rounded-md shadow-sm">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={cn("w-full flex justify-center items-center gap-2 py-2 sm:py-3 px-4 text-base sm:text-xl font-semibold rounded-md text-white transition duration-150 ease-in-out",
                      isLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-accent-blue hover:bg-accent-blue-hover focus:outline-none focus:border-accent-blue focus:shadow-outline-blue active:bg-accent-blue-shadow")}
                  >
                    {isLoading ? (
                      <>
                        <span>Resetting Password</span>
                        <div className="animate-spin rounded-full border-t-2 border-white h-4 w-4"></div>
                      </>
                    ) : (
                      <>
                        <span>Reset Password</span>
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19.5 3.5H5.5C4.39543 3.5 3.5 4.39543 3.5 5.5V19.5C3.5 20.6046 4.39543 21.5 5.5 21.5H19.5C20.6046 21.5 21.5 20.6046 21.5 19.5V5.5C21.5 4.39543 20.6046 3.5 19.5 3.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8.5 12.5H16.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12.5 16.5L16.5 12.5L12.5 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </button>
                </span>
              </div>
            </form>
          )}
        </div>

        <p className="text-sm text-center mt-2">
          <Link to="/login" className="pl-1 text-accent-blue hover:underline">
            Back to login ?
          </Link>
        </p>
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar
        transition={Zoom}
        closeOnClick
        draggable
        closeButton={false}
        limit={1}
      />
    </div>
  );
}

export default ResetPassword;
